import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, API_BASE } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  Store, Settings as SettingsIcon, LogOut, User,
  Delete, Trash2, ReceiptText, History, Users, Printer,
  X, Plus, CheckCircle2, ChevronRight, Zap, Eye, Maximize2, Minimize2,
  Menu, Shuffle, FileText, BarChart3, PlayCircle, MoreVertical, Check,
} from "lucide-react";
import { toast } from "sonner";
import { printTicket } from "../components/PrintableTicket";
import CustomerView from "../components/CustomerView";

// Small per-operator visual accent used by the mobile step card (colored dot).
const OPERATOR_DOT = {
  NAC: "bg-emerald-500",
  LEIDSA: "bg-sky-500",
  LOTEKA: "bg-sky-400",
  PRIMERA: "bg-red-500",
  REAL: "bg-amber-400",
  ANG: "bg-yellow-500",
  NY: "bg-blue-700",
  FL: "bg-orange-500",
  LOTEDOM: "bg-fuchsia-500",
  SUERTE: "bg-teal-500",
};
const DEFAULT_DOT = "bg-slate-400";

// Distinct visual identity per operator so the tile grid mirrors the reference.
const OPERATOR_STYLES = {
  NAC:     { bg: "bg-emerald-600", fg: "text-white", accent: "Lotería Nacional", tag: "Tu sueño, nuestra realidad" },
  LEIDSA:  { bg: "bg-white", fg: "text-blue-700 border border-slate-200", accent: "LEIDSA", tag: "Tu única Loto" },
  LOTEKA:  { bg: "bg-sky-500", fg: "text-white", accent: "Loteka", tag: "Te toca" },
  PRIMERA: { bg: "bg-white", fg: "text-red-600 border border-slate-200", accent: "La Primera", tag: "Pega más" },
  REAL:    { bg: "bg-amber-400", fg: "text-blue-800", accent: "REAL", tag: "Tu lotería de siempre" },
  ANG:     { bg: "bg-white", fg: "text-yellow-500 border border-slate-200", accent: "Anguilla", tag: "Más oportunidades" },
  NY:      { bg: "bg-white", fg: "text-blue-900 border border-slate-200", accent: "New York", tag: "Juega en grande" },
  FL:      { bg: "bg-white", fg: "text-orange-600 border border-slate-200", accent: "Florida Lotto", tag: "A otro nivel" },
  LOTEDOM: { bg: "bg-fuchsia-600", fg: "text-white", accent: "LoteDom", tag: "El grande de siempre" },
  SUERTE:  { bg: "bg-teal-500", fg: "text-white", accent: "La Suerte", tag: "Tu suerte hoy" },
};
const DEFAULT_STYLE = { bg: "bg-slate-100", fg: "text-slate-800", accent: "", tag: "" };

const PLAY_STYLES = {
  QUINIELA: { bg: "bg-emerald-600 hover:bg-emerald-700", desc: "1 Número" },
  PALE:     { bg: "bg-blue-600 hover:bg-blue-700",       desc: "2 Números" },
  TRIPLETA: { bg: "bg-red-600 hover:bg-red-700",         desc: "3 Números" },
  SUPERPALE:{ bg: "bg-slate-500 hover:bg-slate-600",     desc: "2 Loterías" },
  PEGA3:    { bg: "bg-purple-600 hover:bg-purple-700",   desc: "3 Dígitos" },
  PEGA4:    { bg: "bg-indigo-600 hover:bg-indigo-700",   desc: "4 Dígitos" },
  LOTO:     { bg: "bg-orange-600 hover:bg-orange-700",   desc: "5 Números" },
};

function drNowHM() {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "America/Santo_Domingo",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(new Date());
  const hh = parts.find((p) => p.type === "hour").value;
  const mm = parts.find((p) => p.type === "minute").value;
  return `${hh}:${mm}`;
}

function formatDrawTime(t) {
  if (!t || !/^\d{2}:\d{2}$/.test(t)) return "";
  const [h, m] = t.split(":");
  const hh = parseInt(h, 10);
  const ampm = hh >= 12 ? "PM" : "AM";
  const h12 = ((hh + 11) % 12) + 1;
  return `${h12}:${m} ${ampm}`;
}

function parseNumbers(raw) {
  return (raw || "").split(/[-\s,]+/).filter(Boolean);
}

// Auto-split a raw string like "050302" into ["05","03","02"] when the play
// requires N numbers of D digits and the user entered N*D digits without any
// separator. Falls back to the raw list if the length doesn't divide evenly.
function splitByGameDigits(raw, playType, gameDigits) {
  const clean = (raw || "").trim();
  if (!clean) return [];
  if (/[-\s,]/.test(clean)) return parseNumbers(clean);
  const required = playType?.numbers_required || 1;
  if (required <= 1) return [clean];
  if (gameDigits && clean.length === required * gameDigits) {
    const parts = [];
    for (let i = 0; i < required; i++) {
      parts.push(clean.slice(i * gameDigits, (i + 1) * gameDigits));
    }
    return parts;
  }
  return [clean];
}

export default function POS() {
  const { user, logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const [operators, setOperators] = useState([]);
  const [games, setGames] = useState([]);
  const [playTypes, setPlayTypes] = useState([]);
  const [settings, setSettings] = useState({});
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedPT, setSelectedPT] = useState(null);
  const [currentNumber, setCurrentNumber] = useState("");
  const [currentAmount, setCurrentAmount] = useState("");
  const [activeField, setActiveField] = useState("number"); // "number" | "amount"
  const [cart, setCart] = useState([]); // {id, game_id, play_type_id, display, amount, game_name, pt_name}
  const [nowTick, setNowTick] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [lastTicket, setLastTicket] = useState(null);
  const [customerViewOpen, setCustomerViewOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen({ navigationUI: "hide" });
      } else {
        await document.exitFullscreen();
      }
    } catch {
      toast.error("El navegador no permite modo pantalla completa aquí");
    }
  };

  useEffect(() => {
    api.get("/operators").then(({ data }) => setOperators(data.filter((o) => o.active)));
    api.get("/games").then(({ data }) => setGames(data.filter((g) => g.active)));
    api.get("/play-types").then(({ data }) => setPlayTypes(data));
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNowTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const drTime = useMemo(() => {
    void nowTick;
    const now = new Date();
    const dateFmt = new Intl.DateTimeFormat("es-DO", {
      timeZone: "America/Santo_Domingo",
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    }).format(now);
    const timeFmt = new Intl.DateTimeFormat("es-DO", {
      timeZone: "America/Santo_Domingo",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
    }).format(now);
    return { date: dateFmt, time: timeFmt };
  }, [nowTick]);

  const isGameOpen = (g) => {
    if (!g?.closing_time) return true;
    return drNowHM() < g.closing_time;
  };

  // Games grouped by operator, keeping only open games (respect settings default)
  const openGamesByOp = useMemo(() => {
    const hideClosed = settings?.pos_hide_closed_games ?? true;
    const filtered = games.filter((g) => (hideClosed ? isGameOpen(g) : true));
    const map = new Map();
    for (const g of filtered) {
      const arr = map.get(g.operator_id) || [];
      arr.push(g);
      map.set(g.operator_id, arr);
    }
    // Sort each group by closing_time
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.closing_time || "").localeCompare(b.closing_time || ""));
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [games, settings, nowTick]);

  const operatorsForGrid = useMemo(
    () => operators.filter((o) => (openGamesByOp.get(o.id) || []).length > 0),
    [operators, openGamesByOp]
  );

  const selectedGameObj = useMemo(
    () => games.find((g) => g.id === selectedGame),
    [games, selectedGame]
  );

  // When operator changes → pick next open game automatically
  useEffect(() => {
    if (!selectedOperator) { setSelectedGame(null); return; }
    const gs = openGamesByOp.get(selectedOperator) || [];
    if (gs.length > 0) setSelectedGame(gs[0].id);
    else setSelectedGame(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOperator]);

  // Available play types filtered by game digit count
  const availablePTs = useMemo(() => {
    if (!selectedGameObj) return playTypes;
    const d = selectedGameObj.number_digits;
    return playTypes.filter((p) => {
      if (d === 2) return ["QUINIELA", "PALE", "TRIPLETA", "SUPERPALE"].includes(p.code);
      if (d === 3) return p.code === "PEGA3";
      if (d === 4) return p.code === "PEGA4";
      if (d === 5) return p.code === "LOTO";
      return true;
    });
  }, [playTypes, selectedGameObj]);

  useEffect(() => {
    if (!availablePTs || availablePTs.length === 0) {
      setSelectedPT(null);
      return;
    }
    if (!selectedPT || !availablePTs.some((p) => p.id === selectedPT.id)) {
      setSelectedPT(availablePTs[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availablePTs]);

  // Auto-format the raw digit string into groups separated by dashes based on the
  // currently-selected play type + game digit width. E.g. Tripleta on 2-digit
  // game formats "050302" → "05-03-02".
  const formatNumberString = (raw) => {
    if (!selectedGameObj || !selectedPT) return raw;
    const digits = selectedGameObj.number_digits;
    const groups = selectedPT.numbers_required;
    const clean = (raw || "").replace(/-/g, "").slice(0, digits * groups);
    const parts = [];
    for (let i = 0; i < clean.length; i += digits) {
      parts.push(clean.slice(i, i + digits));
    }
    return parts.join("-");
  };

  // ---- Keypad handling ----
  const pushDigit = (d) => {
    if (activeField === "amount") {
      setCurrentAmount((v) => {
        if (d === "." && v.includes(".")) return v;
        if (d === "-") return v; // dash doesn't belong in amount
        return v + d;
      });
      return;
    }
    // Number field: apply progressive auto-format.
    setCurrentNumber((v) => {
      if (d === "-") {
        // If auto-format is doing the job, ignore manual dashes. Only allow the
        // user to insert dashes when they've disabled play type detection.
        if (selectedGameObj && selectedPT) return v;
        return v + d;
      }
      if (d === ".") return v; // dot not valid for numbers
      const combined = (v.replace(/-/g, "") + d);
      // Cap at max digits when a play type + game are selected.
      if (selectedGameObj && selectedPT) {
        const max = selectedGameObj.number_digits * selectedPT.numbers_required;
        if (combined.length > max) return v;
        return formatNumberString(combined);
      }
      return v + d;
    });
  };
  const popDigit = () => {
    if (activeField === "amount") {
      setCurrentAmount((v) => v.slice(0, -1));
      return;
    }
    setCurrentNumber((v) => {
      // Strip trailing dashes first, then remove one digit, then reformat.
      let clean = (v || "").replace(/-/g, "");
      clean = clean.slice(0, -1);
      return formatNumberString(clean);
    });
  };
  const clearInput = () => {
    setCurrentNumber("");
    setCurrentAmount("");
  };

  // Re-format when play type or game changes so the existing digits regroup.
  useEffect(() => {
    setCurrentNumber((v) => (v ? formatNumberString(v) : v));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPT, selectedGameObj]);

  // Add current entry to cart
  const addToCart = () => {
    if (!selectedGameObj) return toast.error("Selecciona una lotería");
    const pt = selectedPT || availablePTs[0];
    if (!pt) return toast.error("Selecciona un tipo de jugada");
    const digits = selectedGameObj.number_digits;
    const nums = splitByGameDigits(currentNumber, pt, digits);
    if (nums.length !== pt.numbers_required) {
      return toast.error(`Ingresa ${pt.numbers_required} número(s) para ${pt.name}. Ej: ${Array(pt.numbers_required).fill("0".repeat(digits)).join("-")}`);
    }
    if (nums.some((n) => n.length !== digits)) {
      return toast.error(`Cada número debe tener ${digits} dígitos`);
    }
    const amt = parseFloat(currentAmount);
    if (!Number.isFinite(amt) || amt <= 0) return toast.error("Ingresa un monto válido");
    setCart((prev) => [
      ...prev,
      {
        id: Math.random().toString(36).slice(2),
        game_id: selectedGameObj.id,
        game_name: selectedGameObj.name,
        game_time: formatDrawTime(selectedGameObj.draw_time),
        play_type_id: pt.id,
        play_type_code: pt.code,
        pt_name: pt.name,
        numbers: nums,
        display: nums.join("-"),
        amount: amt,
      },
    ]);
    setCurrentNumber("");
    setCurrentAmount("");
    setActiveField("number");
  };

  const removeFromCart = (id) => setCart((prev) => prev.filter((c) => c.id !== id));

  const cartTotal = cart.reduce((s, c) => s + c.amount, 0);

  const submitSale = async () => {
    if (cart.length === 0) return toast.error("No hay jugadas en el ticket");
    setSubmitting(true);
    try {
      const plays = cart.map((c) => ({
        game_id: c.game_id,
        play_type_id: c.play_type_id,
        numbers: c.numbers,
        amount: c.amount,
      }));
      const { data } = await api.post("/tickets", { plays });
      setLastTicket(data);
      setCart([]);
      setCurrentNumber("");
      setCurrentAmount("");
      toast.success(`Ticket ${data.ticket_number} emitido`);
      setTimeout(() => printTicket(data), 300);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Quick amount chips from settings ----
  const quickAmounts = Array.isArray(settings?.pos_quick_amounts) && settings.pos_quick_amounts.length > 0
    ? settings.pos_quick_amounts : [5, 10, 25, 50, 100];

  const businessName = settings?.receipt_business_name || "Mi Banca";
  const logoUrl = settings?.receipt_logo_path
    ? `${API_BASE}/settings/logo?consortium_id=${encodeURIComponent(settings?.consortium_id || "")}&t=${encodeURIComponent(settings.receipt_logo_path)}`
    : null;

  if (!user?.branch_id) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-8">
        <div className="max-w-md bg-white border border-slate-200 rounded-sm p-8 text-center" data-testid="no-branch-warning">
          <div className="w-12 h-12 bg-yellow-100 text-yellow-700 rounded-sm flex items-center justify-center mx-auto mb-3">
            <Store className="w-6 h-6" />
          </div>
          <div className="font-bold text-slate-900">Sin sucursal asignada</div>
          <div className="text-sm text-slate-600 mt-2">
            Debes estar asignado a una sucursal para emitir tickets. Contacta al administrador.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Desktop top navbar — hidden on mobile; mobile uses its own header */}
      <header className="hidden lg:flex bg-slate-900 text-white px-4 sm:px-6 py-3 items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-slate-800 rounded-sm flex items-center justify-center shrink-0">
            {logoUrl ? (
              <img src={logoUrl} alt="" className="max-w-full max-h-full object-contain" />
            ) : (
              <Store className="w-6 h-6 text-blue-400" strokeWidth={2.5} />
            )}
          </div>
          <div className="min-w-0">
            <div className="font-black text-lg tracking-tight leading-none" style={{ fontFamily: "Manrope" }}>
              {businessName}
            </div>
            <div className="text-[10px] text-slate-400 font-mono uppercase tracking-wider mt-0.5">
              {settings?.receipt_footer_line3 || "Tu suerte, siempre aquí"}
            </div>
          </div>
        </div>

        <div className="hidden md:flex flex-col items-center flex-1 min-w-0">
          <div className="text-sm text-slate-300 capitalize">{drTime.date}</div>
          <div className="text-xl font-mono font-bold tracking-wider" data-testid="pos-clock">{drTime.time}</div>
        </div>

        <div className="flex items-center gap-3 ml-auto">
          <div className="hidden sm:flex items-center gap-2 text-right">
            <User className="w-4 h-4 text-slate-400" />
            <div>
              <div className="text-xs text-slate-400 leading-none">Usuario</div>
              <div className="text-sm font-bold leading-tight">{user?.name || user?.email}</div>
            </div>
          </div>
          <button
            onClick={() => setCustomerViewOpen(true)}
            className="flex flex-col items-center gap-0.5 text-slate-300 hover:text-white p-1"
            data-testid="pos-nav-customer-view"
            title="Vista para el cliente"
          >
            <Eye className="w-5 h-5" />
            <span className="text-[10px] hidden sm:block">Modo Cliente</span>
          </button>
          <button
            onClick={toggleFullscreen}
            className="flex flex-col items-center gap-0.5 text-slate-300 hover:text-white p-1"
            data-testid="pos-nav-fullscreen"
            title={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
          >
            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            <span className="text-[10px] hidden sm:block">{isFullscreen ? "Salir" : "Kiosco"}</span>
          </button>
          <button
            onClick={() => navigate("/settings")}
            className="flex flex-col items-center gap-0.5 text-slate-300 hover:text-white p-1"
            data-testid="pos-nav-settings"
            title="Configuración"
          >
            <SettingsIcon className="w-5 h-5" />
            <span className="text-[10px] hidden sm:block">Configuración</span>
          </button>
          <button
            onClick={logout}
            className="flex flex-col items-center gap-0.5 text-slate-300 hover:text-white p-1"
            data-testid="pos-nav-logout"
            title="Salir"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-[10px] hidden sm:block">Salir</span>
          </button>
        </div>
      </header>

      <CustomerView
        open={customerViewOpen}
        onClose={() => setCustomerViewOpen(false)}
        cart={cart}
        cartTotal={cartTotal}
        businessName={businessName}
        lastTicket={lastTicket}
      />

      {/* ============================================================
          MOBILE LAYOUT  — chooses between classic (blue) and dark steps
          ============================================================ */}
      {settings?.pos_mobile_style === "dark" ? (
        <POSMobile
          state={{
            businessName, logoUrl, operators, openGamesByOp, operatorsForGrid,
            selectedOperator, setSelectedOperator, setSelectedPT,
            availablePTs, selectedPT, selectedGameObj,
            currentNumber, setCurrentNumber, currentAmount, setCurrentAmount,
            quickAmounts, pushDigit, popDigit, clearInput, addToCart,
            cart, cartTotal, submitSale, submitting, lastTicket,
            formatNumberString, hasRole, navigate, logout,
            setCustomerViewOpen,
          }}
        />
      ) : (
        <POSMobileClassic
          state={{
            businessName, operators, openGamesByOp, operatorsForGrid,
            selectedOperator, setSelectedOperator, setSelectedGame, selectedGame,
            setSelectedPT, availablePTs, selectedPT, selectedGameObj,
            currentNumber, setCurrentNumber, currentAmount, setCurrentAmount,
            addToCart, removeFromCart,
            cart, cartTotal, submitSale, submitting, lastTicket,
            formatNumberString, hasRole, navigate, logout,
            setCustomerViewOpen, setCart,
          }}
        />
      )}

      {/* ============================================================
          DESKTOP LAYOUT  (grid + sidebar) — hidden on < lg
          ============================================================ */}
      {/* Main body */}
      <div className="hidden lg:grid flex-1 grid-cols-[minmax(0,1fr)_360px] gap-4 p-4">
        {/* LEFT: cards + play types + keypad */}
        <div className="space-y-3 min-w-0">
          {/* Operator cards grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="pos-operators-grid">
            {operatorsForGrid.length === 0 && (
              <div className="col-span-full bg-white border border-slate-200 rounded-sm p-6 text-center text-sm text-slate-500" data-testid="pos-no-open">
                No hay loterías abiertas ahora mismo.
              </div>
            )}
            {operatorsForGrid.map((op) => {
              const st = OPERATOR_STYLES[op.code] || DEFAULT_STYLE;
              const isSel = selectedOperator === op.id;
              const gs = openGamesByOp.get(op.id) || [];
              const opLogo = op.logo_path ? `${API_BASE}/operators/${op.id}/logo` : null;
              return (
                <button
                  key={op.id}
                  onClick={() => { setSelectedOperator(op.id); setSelectedPT(null); }}
                  className={`relative rounded-sm p-3 sm:p-4 h-28 sm:h-32 flex flex-col items-center justify-center transition-all overflow-hidden ${st.bg} ${st.fg} ${
                    isSel ? "ring-4 ring-blue-500 ring-offset-1 scale-[1.02]" : "hover:scale-[1.01]"
                  }`}
                  data-testid={`pos-op-${op.code}`}
                >
                  {opLogo ? (
                    <img src={opLogo} alt={op.name} className="max-h-16 max-w-full object-contain" data-testid={`pos-op-logo-${op.code}`} />
                  ) : (
                    <>
                      <div className="font-black tracking-tight text-lg sm:text-xl leading-tight text-center" style={{ fontFamily: "Manrope" }}>
                        {op.name}
                      </div>
                      <div className="text-[10px] font-semibold uppercase tracking-widest opacity-80 mt-0.5">{st.tag}</div>
                    </>
                  )}
                  <div className="absolute top-1.5 right-1.5 text-[10px] font-mono bg-black/20 backdrop-blur px-1.5 py-0.5 rounded-sm text-white">
                    {gs.length} abierta{gs.length === 1 ? "" : "s"}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Games chip row for selected operator (if >1) */}
          {selectedOperator && openGamesByOp.get(selectedOperator) && openGamesByOp.get(selectedOperator).length > 1 && (
            <div className="flex gap-2 flex-wrap bg-white border border-slate-200 rounded-sm p-2" data-testid="pos-games-chips">
              {openGamesByOp.get(selectedOperator).map((g) => (
                <button
                  key={g.id}
                  onClick={() => setSelectedGame(g.id)}
                  className={`px-3 py-1.5 rounded-sm text-xs font-bold ${
                    selectedGame === g.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                  data-testid={`pos-game-chip-${g.code}`}
                >
                  {g.name.replace(op_name_prefix(operators, g.operator_id), "").trim() || g.name} · {formatDrawTime(g.draw_time)}
                </button>
              ))}
            </div>
          )}

          {/* Play type buttons */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="pos-play-types-row">
            {availablePTs.map((p) => {
              const st = PLAY_STYLES[p.code] || { bg: "bg-slate-600 hover:bg-slate-700", desc: `${p.numbers_required} número(s)` };
              const isSel = selectedPT?.id === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedPT(p)}
                  className={`rounded-sm py-2.5 px-3 text-white font-black flex flex-col items-center justify-center transition-transform ${st.bg} ${
                    isSel ? "ring-4 ring-blue-500 ring-offset-1 scale-[1.02]" : ""
                  }`}
                  data-testid={`pos-pt-${p.code}`}
                >
                  <span className="text-base leading-none" style={{ fontFamily: "Manrope" }}>{p.name}</span>
                  <span className="text-[10px] opacity-90 mt-0.5">{st.desc}</span>
                </button>
              );
            })}
          </div>

          {/* Number + Amount active fields */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setActiveField("number")}
              className={`bg-white border-2 rounded-sm p-3 text-left ${
                activeField === "number" ? "border-blue-500" : "border-slate-200"
              }`}
              data-testid="pos-field-number"
            >
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Número</div>
                {selectedGameObj && selectedPT && (
                  <div className="text-[9px] font-mono text-slate-400" data-testid="pos-number-hint">
                    Ej: {Array(selectedPT.numbers_required).fill("0".repeat(selectedGameObj.number_digits)).join("-")}
                  </div>
                )}
              </div>
              <div className="font-mono font-black text-2xl text-slate-950 mt-1 min-h-[32px]">
                {currentNumber || <span className="text-slate-300">—</span>}
              </div>
            </button>
            <button
              onClick={() => setActiveField("amount")}
              className={`bg-white border-2 rounded-sm p-3 text-left ${
                activeField === "amount" ? "border-blue-500" : "border-slate-200"
              }`}
              data-testid="pos-field-amount"
            >
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Monto RD$</div>
              <div className="font-mono font-black text-2xl text-slate-950 mt-1 min-h-[32px]">
                {currentAmount || <span className="text-slate-300">—</span>}
              </div>
            </button>
          </div>

          {/* Quick amount chips */}
          <div className="flex flex-wrap gap-1.5" data-testid="pos-quick-amounts">
            {quickAmounts.map((a) => (
              <button
                key={a}
                onClick={() => { setActiveField("amount"); setCurrentAmount(String(a)); }}
                className="text-xs font-bold font-mono px-3 py-1.5 border border-slate-300 rounded-sm bg-white hover:bg-slate-900 hover:text-white transition-colors"
                data-testid={`pos-quick-${a}`}
              >
                RD${a}
              </button>
            ))}
          </div>

          {/* Keypad */}
          <div className="grid grid-cols-5 gap-1.5" data-testid="pos-keypad">
            {["1","2","3","4","5"].map((d) => (
              <KeypadButton key={d} label={d} onClick={() => pushDigit(d)} data-testid={`pos-key-${d}`} />
            ))}
            {["6","7","8","9","0"].map((d) => (
              <KeypadButton key={d} label={d} onClick={() => pushDigit(d)} data-testid={`pos-key-${d}`} />
            ))}
            <KeypadButton label="-" onClick={() => pushDigit("-")} className="bg-slate-200 hover:bg-slate-300 text-slate-800 text-xl" data-testid="pos-key-dash" />
            <KeypadButton label="." onClick={() => pushDigit(".")} className="bg-slate-200 hover:bg-slate-300 text-slate-800 text-xl" data-testid="pos-key-dot" />
            <button onClick={popDigit} className="h-14 rounded-sm bg-slate-800 hover:bg-slate-900 text-white flex items-center justify-center" data-testid="pos-key-back">
              <Delete className="w-5 h-5" />
            </button>
            <button onClick={clearInput} className="h-14 rounded-sm bg-orange-500 hover:bg-orange-600 text-white font-black text-sm" data-testid="pos-key-clear">
              Limpiar
            </button>
            <button
              onClick={addToCart}
              className="h-14 rounded-sm bg-blue-600 hover:bg-blue-700 text-white font-black text-sm flex items-center justify-center gap-1"
              data-testid="pos-add-to-cart"
            >
              <Plus className="w-4 h-4" /> Agregar
            </button>
          </div>

          {/* Bottom actions */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
            <FootAction icon={ReceiptText} label="Consultar Resultados" onClick={() => navigate("/results")} testId="pos-nav-results" />
            <FootAction icon={History} label={hasRole("cashier") ? "Mis Tickets" : "Historial"} onClick={() => navigate("/tickets")} testId="pos-nav-history" />
            <FootAction icon={Users} label="Pagar Premio" onClick={() => navigate("/payouts")} testId="pos-nav-payouts" />
            <FootAction icon={Printer} label="Reimprimir" onClick={() => lastTicket ? printTicket(lastTicket, { isReprint: true }) : toast.info("No hay ticket reciente")} testId="pos-nav-reprint" />          </div>
        </div>

        {/* RIGHT: Cart / Ticket */}
        <aside className="bg-white border border-slate-200 rounded-sm flex flex-col self-start lg:sticky lg:top-4 max-h-[calc(100vh-100px)]" data-testid="pos-cart">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-900 text-white">
            <div className="font-black tracking-tight text-lg" style={{ fontFamily: "Manrope" }}>Ticket Actual</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
              {selectedGameObj ? `${selectedGameObj.name} · ${formatDrawTime(selectedGameObj.draw_time)}` : "Elige una lotería"}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto min-h-[240px]">
            {cart.length === 0 ? (
              <div className="p-8 text-center flex flex-col items-center gap-2 h-full justify-center">
                <div className="w-14 h-14 bg-slate-100 rounded-sm flex items-center justify-center text-slate-300">
                  <ReceiptText className="w-7 h-7" />
                </div>
                <div className="font-bold text-slate-500">No hay jugadas agregadas</div>
                <div className="text-xs text-slate-400 max-w-[220px]">Seleccione una lotería y agregue sus jugadas con el teclado</div>
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] font-bold uppercase tracking-widest text-slate-500 bg-slate-50">
                    <th className="text-left px-2 py-2 w-8">#</th>
                    <th className="text-left px-2 py-2">Jugada</th>
                    <th className="text-right px-2 py-2">Monto</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody data-testid="pos-cart-rows">
                  {cart.map((c, idx) => (
                    <tr key={c.id} className="border-t border-slate-100" data-testid={`pos-cart-row-${idx}`}>
                      <td className="px-2 py-2 font-mono text-slate-500">{idx + 1}</td>
                      <td className="px-2 py-2">
                        <div className="font-mono font-black text-slate-950 text-base tracking-wide">{c.display}</div>
                        <div className="text-[10px] text-slate-500">{c.pt_name} · {c.game_time}</div>
                      </td>
                      <td className="px-2 py-2 text-right font-mono font-bold">RD${c.amount.toFixed(2)}</td>
                      <td className="px-1">
                        <button
                          onClick={() => removeFromCart(c.id)}
                          className="w-6 h-6 rounded-sm text-slate-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center"
                          data-testid={`pos-cart-remove-${idx}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="border-t border-slate-200 p-3 space-y-2 bg-slate-50">
            <div className="flex items-baseline justify-between">
              <div className="text-sm font-bold text-slate-900">Total:</div>
              <div className="text-2xl font-black font-mono text-slate-950" data-testid="pos-cart-total">
                RD${cartTotal.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <button
              onClick={submitSale}
              disabled={cart.length === 0 || submitting}
              className="w-full h-12 rounded-sm bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white font-black tracking-wide flex items-center justify-center gap-2"
              data-testid="pos-submit"
            >
              <Printer className="w-5 h-5" />
              {submitting ? "EMITIENDO…" : "VENDER TICKET"}
            </button>
            {lastTicket && (
              <div className="flex items-center justify-between text-[10px] font-mono text-slate-500 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1">
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-emerald-700" />
                  Último: <b className="text-emerald-800">{lastTicket.ticket_number}</b>
                </span>
                <a href={`/t/${encodeURIComponent(lastTicket.ticket_number)}${lastTicket.share_token ? `?v=${encodeURIComponent(lastTicket.share_token)}` : ""}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 font-bold underline">
                  Ver
                </a>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function op_name_prefix(operators, opId) {
  const op = operators.find((o) => o.id === opId);
  return op?.name || "";
}

function KeypadButton({ label, onClick, className = "", ...rest }) {
  return (
    <button
      onClick={onClick}
      className={`h-14 rounded-sm bg-white hover:bg-slate-50 border border-slate-200 text-slate-900 font-black text-2xl transition-colors ${className}`}
      {...rest}
    >
      {label}
    </button>
  );
}

function FootAction({ icon: Icon, label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className="bg-white border border-slate-200 rounded-sm h-12 px-3 flex items-center gap-2 hover:bg-slate-50 text-slate-800"
      data-testid={testId}
    >
      <Icon className="w-4 h-4 text-slate-500 shrink-0" />
      <span className="text-xs font-bold text-left leading-tight truncate">{label}</span>
    </button>
  );
}

// ============================================================================
//  POS MOBILE  — step-based dark UI (reference-driven)
// ============================================================================
function POSMobile({ state }) {
  const {
    businessName, operatorsForGrid, openGamesByOp,
    selectedOperator, setSelectedOperator, setSelectedPT,
    availablePTs, selectedPT, selectedGameObj,
    currentNumber, setCurrentNumber, currentAmount, setCurrentAmount,
    quickAmounts, addToCart,
    cart, cartTotal, submitSale, submitting, lastTicket,
    formatNumberString, hasRole, navigate, logout,
  } = state;

  const [menuOpen, setMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("jugar"); // visual-only state for bottom nav

  const pushDigitMobile = (d) => {
    // Mobile: digits ALWAYS go to the number field (amount uses chips/direct input).
    setCurrentNumber((v) => {
      const combined = (v || "").replace(/-/g, "") + d;
      if (selectedGameObj && selectedPT) {
        const max = selectedGameObj.number_digits * selectedPT.numbers_required;
        if (combined.length > max) return v;
        return formatNumberString(combined);
      }
      return v + d;
    });
  };
  const popDigitMobile = () => {
    setCurrentNumber((v) => {
      let clean = (v || "").replace(/-/g, "");
      clean = clean.slice(0, -1);
      return formatNumberString(clean);
    });
  };
  const randomize = () => {
    if (!selectedGameObj || !selectedPT) return;
    const digits = selectedGameObj.number_digits;
    const groups = selectedPT.numbers_required;
    const parts = [];
    for (let i = 0; i < groups; i++) {
      let s = "";
      for (let j = 0; j < digits; j++) s += Math.floor(Math.random() * 10);
      parts.push(s);
    }
    setCurrentNumber(parts.join("-"));
  };

  const clearAll = () => {
    setCurrentNumber("");
    setCurrentAmount("");
  };

  const needed = selectedPT?.numbers_required || 1;
  const dig = selectedGameObj?.number_digits || 2;
  const rangeLabel = `${"0".repeat(dig)} - ${"9".repeat(dig)}`;

  const canAdd = Boolean(selectedGameObj && selectedPT && currentNumber && currentAmount);
  const canPrint = cart.length > 0 && !submitting;

  const handlePrintTicket = async () => {
    if (canAdd) await addToCart();
    if (cart.length === 0 && !canAdd) {
      toast.error("Agrega al menos una jugada");
      return;
    }
    await submitSale();
  };

  return (
    <div className="lg:hidden flex-1 flex flex-col bg-slate-900 text-white min-h-screen" data-testid="pos-mobile">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-slate-900 border-b border-slate-800 px-4 pt-3 pb-3 flex items-center gap-3">
        <button
          onClick={() => setMenuOpen(true)}
          className="p-1.5 -ml-1.5 rounded-md hover:bg-slate-800 active:bg-slate-700"
          data-testid="pos-mobile-menu-btn"
          aria-label="Menú"
        >
          <Menu className="w-6 h-6" strokeWidth={2.25} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-xl font-black tracking-tight leading-none" style={{ fontFamily: "Manrope" }}>
            Jugadas
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5 truncate">
            {businessName} · Sistema de Apuestas
          </div>
        </div>
        <button
          onClick={() => navigate("/tickets")}
          className="relative p-1.5 rounded-md hover:bg-slate-800"
          data-testid="pos-mobile-history-btn"
          aria-label="Historial"
        >
          <FileText className="w-6 h-6 text-slate-200" strokeWidth={2.25} />
          {cart.length > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-emerald-500 text-slate-950 text-[10px] font-black rounded-full w-4 h-4 flex items-center justify-center">
              {cart.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setMenuOpen(true)}
          className="p-1.5 rounded-md hover:bg-slate-800"
          data-testid="pos-mobile-kebab"
          aria-label="Más opciones"
        >
          <MoreVertical className="w-6 h-6 text-slate-200" strokeWidth={2.25} />
        </button>
      </header>

      {/* Scrollable body */}
      <main className="flex-1 overflow-y-auto px-4 pt-4 pb-28 space-y-4">
        {/* STEP 1 — Monto a Jugar */}
        <section className="bg-white text-slate-900 rounded-2xl shadow-sm overflow-hidden" data-testid="pos-mobile-step-amount">
          <div className="flex items-center gap-3 px-4 pt-4">
            <StepBadge n={1} />
            <div className="flex-1 flex items-center justify-between">
              <h2 className="text-base font-black" style={{ fontFamily: "Manrope" }}>Monto a Jugar</h2>
              <span className="text-xs font-bold text-slate-400">RD$</span>
            </div>
          </div>
          <div className="px-4 pb-4">
            <div className="mt-2 flex items-center gap-2 bg-slate-100 rounded-xl px-4 py-3">
              <span className="text-base font-bold text-slate-500">RD$</span>
              <input
                inputMode="decimal"
                value={currentAmount}
                onChange={(e) => setCurrentAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                placeholder="0.00"
                className="flex-1 bg-transparent text-3xl font-black text-slate-950 tracking-tight outline-none text-right"
                data-testid="pos-mobile-amount-input"
              />
              {currentAmount && (
                <button
                  onClick={() => setCurrentAmount("")}
                  className="w-9 h-9 bg-slate-200 hover:bg-slate-300 rounded-full flex items-center justify-center"
                  data-testid="pos-mobile-amount-clear"
                  aria-label="Borrar monto"
                >
                  <X className="w-4 h-4 text-slate-700" />
                </button>
              )}
            </div>
            <div className="mt-3 grid grid-cols-5 gap-2">
              {quickAmounts.slice(0, 5).map((a) => {
                const sel = String(a) === currentAmount;
                return (
                  <button
                    key={a}
                    onClick={() => setCurrentAmount(String(a))}
                    data-testid={`pos-mobile-quick-${a}`}
                    className={`py-2.5 rounded-lg font-black text-base transition-colors ${
                      sel ? "bg-blue-600 text-white shadow" : "bg-slate-100 text-slate-800 hover:bg-slate-200"
                    }`}
                  >
                    {a}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* STEP 2 — Selecciona la Lotería (operator) */}
        <section className="bg-white text-slate-900 rounded-2xl shadow-sm overflow-hidden" data-testid="pos-mobile-step-operator">
          <div className="flex items-center gap-3 px-4 pt-4 pb-3">
            <StepBadge n={2} />
            <h2 className="text-base font-black" style={{ fontFamily: "Manrope" }}>Selecciona la Lotería</h2>
          </div>
          <div className="px-4 pb-4 flex gap-2 overflow-x-auto no-scrollbar">
            {operatorsForGrid.length === 0 && (
              <div className="text-sm text-slate-500 italic">No hay loterías abiertas ahora.</div>
            )}
            {operatorsForGrid.map((op) => {
              const sel = selectedOperator === op.id;
              const dot = OPERATOR_DOT[op.code] || DEFAULT_DOT;
              return (
                <button
                  key={op.id}
                  onClick={() => { setSelectedOperator(op.id); setSelectedPT(null); }}
                  data-testid={`pos-mobile-op-${op.code}`}
                  className={`shrink-0 min-w-[108px] px-3 py-3 rounded-xl border-2 flex flex-col items-center justify-center gap-1.5 transition-all ${
                    sel
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "bg-white border-slate-200 text-slate-800 active:bg-slate-50"
                  }`}
                >
                  <span className={`w-5 h-5 rounded-full ${dot} ${sel ? "ring-2 ring-white" : ""}`} />
                  <span className="text-xs font-black leading-tight text-center">{op.name}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* STEP 3 — Tipo de Jugada */}
        <section className="bg-white text-slate-900 rounded-2xl shadow-sm overflow-hidden" data-testid="pos-mobile-step-playtype">
          <div className="flex items-center gap-3 px-4 pt-4 pb-3">
            <StepBadge n={3} />
            <h2 className="text-base font-black" style={{ fontFamily: "Manrope" }}>Tipo de Jugada</h2>
          </div>
          <div className="px-4 pb-4 flex gap-2 overflow-x-auto no-scrollbar">
            {availablePTs.length === 0 && (
              <div className="text-sm text-slate-500 italic">Selecciona una lotería primero.</div>
            )}
            {availablePTs.map((p) => {
              const sel = selectedPT?.id === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedPT(p)}
                  data-testid={`pos-mobile-pt-${p.code}`}
                  className={`shrink-0 min-w-[108px] px-4 py-2.5 rounded-xl border-2 flex flex-col items-center gap-0.5 transition-colors ${
                    sel
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "bg-white border-slate-200 text-slate-700 active:bg-slate-50"
                  }`}
                >
                  <span className="text-sm font-black leading-tight">{p.name}</span>
                  <span className={`text-[11px] font-bold leading-tight ${sel ? "text-blue-100" : "text-slate-500"}`}>
                    ({p.numbers_required} número{p.numbers_required === 1 ? "" : "s"})
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {/* STEP — Keypad */}
        <section className="rounded-2xl overflow-hidden" data-testid="pos-mobile-step-keypad">
          <div className="flex items-center justify-between mb-3 px-1">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400">
                Selecciona tu número
              </div>
              <div className="text-sm font-black text-white">
                ({rangeLabel}){needed > 1 ? ` · ${needed} números` : ""}
              </div>
            </div>
            <button
              onClick={randomize}
              disabled={!selectedGameObj || !selectedPT}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 disabled:opacity-40 active:bg-slate-700"
              data-testid="pos-mobile-random"
            >
              <Shuffle className="w-4 h-4" />
              <span className="text-xs font-black">Aleatorio</span>
            </button>
          </div>

          <div className="grid grid-cols-6 gap-2">
            {/* Big display cell (col-span-1 row-span-2 wide display spans 2 cols for readability) */}
            <div className="col-span-2 row-span-2 bg-white rounded-xl p-3 flex flex-col justify-between min-h-[172px]">
              <div className="text-4xl sm:text-5xl font-black text-slate-950 font-mono tracking-tight break-all leading-none" data-testid="pos-mobile-number-display">
                {currentNumber || <span className="text-slate-300">—</span>}
              </div>
              <button
                onClick={popDigitMobile}
                disabled={!currentNumber}
                className="self-start w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-50 flex items-center justify-center"
                data-testid="pos-mobile-backspace"
                aria-label="Borrar último dígito"
              >
                <X className="w-5 h-5 text-slate-700" />
              </button>
            </div>

            {/* Row 1: digits 1-4 */}
            {["1","2","3","4"].map((d) => (
              <MobileKey key={d} d={d} onClick={() => pushDigitMobile(d)} />
            ))}
            {/* Row 2: digits 5-8 */}
            {["5","6","7","8"].map((d) => (
              <MobileKey key={d} d={d} onClick={() => pushDigitMobile(d)} />
            ))}
            {/* Row 3: digits 9, 0 (full width) */}
            <div className="col-span-3">
              <MobileKey d="9" onClick={() => pushDigitMobile("9")} full />
            </div>
            <div className="col-span-3">
              <MobileKey d="0" onClick={() => pushDigitMobile("0")} full />
            </div>
          </div>
        </section>

        {/* Resumen de la Jugada */}
        <section className="bg-white text-slate-900 rounded-2xl shadow-sm overflow-hidden" data-testid="pos-mobile-summary">
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                <Check className="w-4 h-4 text-white" strokeWidth={3} />
              </div>
              <h2 className="text-base font-black" style={{ fontFamily: "Manrope" }}>Resumen de la Jugada</h2>
            </div>
            <button
              onClick={clearAll}
              className="flex items-center gap-1 text-slate-600 hover:text-red-600 text-sm font-bold"
              data-testid="pos-mobile-clear-summary"
            >
              <Trash2 className="w-4 h-4" />
              Limpiar
            </button>
          </div>
          <div className="grid grid-cols-4 gap-2 px-4 pb-4">
            <SummaryCell label="Lotería" value={
              selectedGameObj ? (
                <span className="flex items-center gap-1">
                  <span className={`w-2.5 h-2.5 rounded-full ${OPERATOR_DOT[(selectedGameObj.operator_code || "").toUpperCase()] || DEFAULT_DOT}`} />
                  <span className="truncate">{selectedGameObj.name}</span>
                </span>
              ) : "—"
            } testId="pos-mobile-summary-game" />
            <SummaryCell label="Tipo" value={selectedPT?.name || "—"} testId="pos-mobile-summary-pt" />
            <SummaryCell label="Número" value={currentNumber || "—"} mono testId="pos-mobile-summary-number" />
            <SummaryCell label="Monto" value={currentAmount ? `RD$ ${currentAmount}` : "—"} mono testId="pos-mobile-summary-amount" />
          </div>
          {cart.length > 0 && (
            <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 space-y-2" data-testid="pos-mobile-cart-list">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                Jugadas en el ticket · {cart.length}
              </div>
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {cart.map((c, idx) => (
                  <li key={c.id} className="flex items-center justify-between text-xs" data-testid={`pos-mobile-cart-${idx}`}>
                    <span className="font-mono font-black text-slate-800">
                      {idx + 1}. {c.display}
                    </span>
                    <span className="font-bold text-slate-600">{c.pt_name}</span>
                    <span className="font-mono font-bold text-slate-900">RD${c.amount.toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <div className="flex justify-between items-baseline pt-1 border-t border-slate-200">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total</span>
                <span className="text-xl font-black font-mono text-slate-950" data-testid="pos-mobile-cart-total">
                  RD${cartTotal.toLocaleString("es-DO", { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          )}
        </section>

        {/* Bottom action buttons (sticky-feel row inside the scroll) */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={async () => {
              if (!canAdd) {
                toast.error("Completa lotería, tipo, número y monto");
                return;
              }
              await addToCart();
            }}
            disabled={!canAdd}
            className="h-16 rounded-2xl bg-slate-800 hover:bg-slate-700 disabled:opacity-40 border border-slate-700 flex items-center justify-center gap-2 text-white font-black transition-colors"
            data-testid="pos-mobile-add-another"
          >
            <Plus className="w-5 h-5" />
            <span className="text-sm leading-tight text-left">Agregar<br />otra jugada</span>
          </button>
          <button
            onClick={handlePrintTicket}
            disabled={!canPrint && !canAdd}
            className="h-16 rounded-2xl bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 flex items-center justify-center gap-2 text-white font-black shadow-lg shadow-emerald-500/20 transition-colors"
            data-testid="pos-mobile-print-ticket"
          >
            <Printer className="w-6 h-6" />
            <span className="text-base tracking-wide">
              {submitting ? "EMITIENDO…" : "IMPRIMIR TICKET"}
            </span>
          </button>
        </div>

        {lastTicket && (
          <div className="flex items-center justify-between text-xs font-mono bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 rounded-xl px-3 py-2" data-testid="pos-mobile-last-ticket">
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4" />
              Último: <b className="text-emerald-100">{lastTicket.ticket_number}</b>
            </span>
            <button
              onClick={() => printTicket(lastTicket, { isReprint: true })}
              className="text-emerald-100 font-black underline"
            >
              Reimprimir
            </button>
          </div>
        )}
      </main>

      {/* Bottom tab bar */}
      <nav className="fixed bottom-0 left-0 right-0 lg:hidden bg-slate-900 border-t border-slate-800 flex items-stretch pb-safe z-40" data-testid="pos-mobile-tabbar">
        <TabButton active={activeTab === "jugar"} onClick={() => setActiveTab("jugar")} icon={PlayCircle} label="Jugar" testId="pos-mobile-tab-jugar" />
        <TabButton onClick={() => navigate("/tickets")} icon={FileText} label="Historial" testId="pos-mobile-tab-historial" />
        <TabButton onClick={() => navigate("/reports")} icon={BarChart3} label="Reportes" testId="pos-mobile-tab-reportes" hidden={!hasRole("super_admin", "admin", "supervisor", "auditor")} />
        <TabButton onClick={() => navigate("/settings")} icon={SettingsIcon} label="Configuración" testId="pos-mobile-tab-config" />
      </nav>

      {/* Slide-in menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 flex" data-testid="pos-mobile-menu-drawer">
          <div className="w-72 bg-slate-900 border-r border-slate-800 flex flex-col animate-in slide-in-from-left duration-200" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
              <div>
                <div className="text-lg font-black" style={{ fontFamily: "Manrope" }}>{businessName}</div>
                <div className="text-[11px] text-slate-400">Menú de usuario</div>
              </div>
              <button onClick={() => setMenuOpen(false)} className="p-1 rounded hover:bg-slate-800" aria-label="Cerrar">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 p-3 space-y-1">
              <MobileMenuItem icon={Eye} label="Modo Cliente" onClick={() => { setMenuOpen(false); state.setCustomerViewOpen(true); }} testId="pos-mobile-menu-customer" />
              <MobileMenuItem icon={ReceiptText} label="Resultados" onClick={() => navigate("/results")} testId="pos-mobile-menu-results" />
              <MobileMenuItem icon={Users} label="Pagar Premio" onClick={() => navigate("/payouts")} testId="pos-mobile-menu-payouts" />
              <MobileMenuItem icon={LogOut} label="Cerrar Sesión" danger onClick={logout} testId="pos-mobile-menu-logout" />
            </div>
          </div>
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            className="flex-1 bg-slate-950/70 backdrop-blur-sm cursor-pointer"
            data-testid="pos-mobile-menu-backdrop"
            aria-label="Cerrar menú"
          />
        </div>
      )}
    </div>
  );
}

function StepBadge({ n }) {
  return (
    <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
      <span className="text-white text-sm font-black" style={{ fontFamily: "Manrope" }}>{n}</span>
    </div>
  );
}

function MobileKey({ d, onClick, full = false }) {
  return (
    <button
      onClick={onClick}
      data-testid={`pos-mobile-key-${d}`}
      className={`${full ? "w-full h-16" : "h-20"} rounded-xl bg-slate-800 hover:bg-slate-700 active:bg-slate-600 border border-slate-700 text-white text-3xl font-black transition-colors`}
    >
      {d}
    </button>
  );
}

function SummaryCell({ label, value, mono = false, testId }) {
  return (
    <div data-testid={testId} className="min-w-0">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-sm font-black text-slate-900 truncate ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function TabButton({ icon: Icon, label, onClick, active, hidden, testId }) {
  if (hidden) return null;
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 ${
        active ? "text-blue-400" : "text-slate-400 active:text-slate-200"
      }`}
    >
      <Icon className="w-5 h-5" strokeWidth={active ? 2.5 : 2} />
      <span className={`text-[11px] font-bold ${active ? "text-blue-400" : "text-slate-300"}`}>{label}</span>
    </button>
  );
}

function MobileMenuItem({ icon: Icon, label, onClick, danger, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-800 active:bg-slate-700 ${
        danger ? "text-red-400" : "text-slate-200"
      }`}
    >
      <Icon className="w-5 h-5" />
      <span className="font-bold text-sm">{label}</span>
    </button>
  );
}


// ============================================================================
//  POS MOBILE CLASSIC  — simple blue calculator-style UI (user reference 2)
// ============================================================================
function POSMobileClassic({ state }) {
  const {
    businessName, operatorsForGrid, openGamesByOp,
    selectedOperator, setSelectedOperator,
    setSelectedGame, selectedGame,
    availablePTs, selectedPT, setSelectedPT, selectedGameObj,
    currentNumber, setCurrentNumber, currentAmount, setCurrentAmount,
    cart, cartTotal, submitSale, submitting, lastTicket, removeFromCart,
    formatNumberString, hasRole, navigate, logout,
    setCustomerViewOpen,
  } = state;

  const [menuOpen, setMenuOpen] = useState(false);
  const [lotteryOpen, setLotteryOpen] = useState(false);
  const [combinarOpen, setCombinarOpen] = useState(false);

  // Field focus for keypad: "number" (default) or "amount"
  const [activeField, setActiveField] = useState("number");

  // Auto-detect play type from how many groups of digits the cashier has typed.
  // 2 digits (one group of game.number_digits) → Quiniela (requires 1 number)
  // 4 digits (two groups)                      → Palé (requires 2 numbers)
  // 6 digits (three groups)                    → Tripleta (requires 3 numbers)
  const autoDetectedPT = useMemo(() => {
    if (!selectedGameObj || availablePTs.length === 0) return null;
    const digits = selectedGameObj.number_digits;
    const cleanLen = (currentNumber || "").replace(/-/g, "").length;
    // Nothing typed yet → nothing to detect (keeps the UI clean after a commit).
    if (cleanLen === 0) return null;
    // Partial group → keep the previously detected PT (fall through to selectedPT).
    if (cleanLen % digits !== 0) return selectedPT || null;
    const groups = cleanLen / digits;
    // Match an active PT that needs exactly this many numbers; prefer ones whose
    // code matches the common ones first (QUINIELA/PALE/TRIPLETA) for stable UX.
    const preferredOrder = { "1": ["QUINIELA"], "2": ["PALE"], "3": ["TRIPLETA"] };
    const prefs = preferredOrder[String(groups)] || [];
    for (const c of prefs) {
      const hit = availablePTs.find((p) => p.code === c && p.numbers_required === groups);
      if (hit) return hit;
    }
    return availablePTs.find((p) => p.numbers_required === groups) || null;
  }, [currentNumber, selectedGameObj, selectedPT, availablePTs]);

  // Reactive formatter that groups a raw digit string using the game's digit
  // width — WITHOUT requiring a play type. This lets the cashier type freely
  // (27, 2785, 278593) and we chunk by dashes on the fly.
  const formatFreeform = (raw) => {
    if (!selectedGameObj) return raw;
    const digits = selectedGameObj.number_digits;
    const clean = (raw || "").replace(/-/g, "");
    const parts = [];
    for (let i = 0; i < clean.length; i += digits) parts.push(clean.slice(i, i + digits));
    return parts.join("-");
  };

  // Maximum raw digits we accept: 3 groups (Tripleta is the largest common PT).
  const maxDigits = selectedGameObj ? selectedGameObj.number_digits * 3 : 6;

  // Keypad input routing
  const pushDigit = (d) => {
    if (activeField === "amount") {
      setCurrentAmount((v) => (d === "." && v.includes(".") ? v : v + d));
      return;
    }
    if (d === ".") return;
    setCurrentNumber((v) => {
      const combined = (v || "").replace(/-/g, "") + d;
      if (combined.length > maxDigits) return v;
      return formatFreeform(combined);
    });
  };
  const popDigit = () => {
    if (activeField === "amount") {
      setCurrentAmount((v) => v.slice(0, -1));
      return;
    }
    setCurrentNumber((v) => {
      let clean = (v || "").replace(/-/g, "");
      clean = clean.slice(0, -1);
      return formatFreeform(clean);
    });
  };

  const randomize = () => {
    if (!selectedGameObj) {
      toast.error("Selecciona una lotería primero");
      return;
    }
    const digits = selectedGameObj.number_digits;
    // Figure out how many groups to generate: use the current PT (auto or
    // manually selected). If none, default to Quiniela (1 group).
    const target = autoDetectedPT || selectedPT || availablePTs.find((p) => p.code === "QUINIELA") || availablePTs[0];
    const groups = (target && target.numbers_required) || 1;
    const parts = [];
    for (let i = 0; i < groups; i++) {
      let s = "";
      for (let j = 0; j < digits; j++) s += Math.floor(Math.random() * 10);
      parts.push(s);
    }
    setCurrentNumber(parts.join("-"));
  };

  // Commit the current entry to the cart (does not print).
  const commitEntry = () => {
    if (!selectedGameObj) return toast.error("Selecciona una lotería"), false;
    const pt = autoDetectedPT || selectedPT;
    if (!pt) return toast.error("Número incompleto para formar una jugada"), false;
    if (!currentNumber) return toast.error("Ingresa el número"), false;
    const amt = parseFloat(currentAmount);
    if (!Number.isFinite(amt) || amt <= 0) return toast.error("Ingresa un monto válido"), false;
    const nums = splitByGameDigits(currentNumber, pt, selectedGameObj.number_digits);
    if (nums.length !== pt.numbers_required) {
      return toast.error(`${pt.name} requiere ${pt.numbers_required} número(s) de ${selectedGameObj.number_digits} dígitos`), false;
    }
    state.setCart((prev) => [
      ...prev,
      {
        id: Math.random().toString(36).slice(2),
        game_id: selectedGameObj.id,
        game_name: selectedGameObj.name,
        game_time: formatDrawTime(selectedGameObj.draw_time),
        play_type_id: pt.id,
        play_type_code: pt.code,
        pt_name: pt.name,
        numbers: nums,
        display: nums.join("-"),
        amount: amt,
      },
    ]);
    setCurrentNumber("");
    setCurrentAmount("");
    setActiveField("number");
    return true;
  };

  // Two-stage ENTRAR button:
  // - Step 1: cashier has typed the number (activeField=number) → move focus to amount.
  // - Step 2: cashier has typed the amount (activeField=amount) → commit to cart.
  const entrar = () => {
    if (activeField === "number") {
      if (!currentNumber) return toast.error("Ingresa el número primero");
      if (!selectedGameObj) return toast.error("Selecciona una lotería");
      setActiveField("amount");
      return;
    }
    // amount stage
    commitEntry();
  };

  const printTicketNow = async () => {
    // If the cashier has a pending entry (number+amount typed) but did not press
    // ENTRAR yet, commit it first so pressing IMPRIMIR directly after typing
    // works just like a classic banca machine.
    if (currentNumber && currentAmount && cart.length === 0) {
      if (!commitEntry()) return;
    } else if (currentNumber && currentAmount) {
      // Also commit so the pending jugada isn't lost on partial carts.
      if (!commitEntry()) return;
    }
    if (cart.length === 0) {
      toast.info("El ticket está vacío");
      return;
    }
    await submitSale();
  };

  const sorteoLabel = selectedGameObj
    ? `Sorteo ${formatDrawTime(selectedGameObj.draw_time)}`
    : "Elige una lotería";

  // COMBINAR is available once there's at least ONE source number (either
  // currently typed OR a Quiniela already in the cart for the same game).
  const canCombine = useMemo(() => {
    if (!selectedGameObj) return false;
    if (currentNumber) return true;
    for (const c of cart) {
      if (c.game_id === selectedGameObj.id) return true;
    }
    return false;
  }, [cart, selectedGameObj, currentNumber]);

  return (
    <div className="lg:hidden flex-1 flex flex-col bg-white text-slate-900 min-h-screen" data-testid="pos-mobile-classic">
      {/* Blue top bar */}
      <header className="sticky top-0 z-30 bg-blue-600 text-white px-3 py-3 flex items-center gap-3 shadow">
        <button
          onClick={() => setMenuOpen(true)}
          className="p-1.5 -ml-1 rounded hover:bg-blue-700 active:bg-blue-800"
          data-testid="pos-classic-menu-btn"
          aria-label="Menú"
        >
          <Menu className="w-6 h-6" strokeWidth={2.5} />
        </button>
        <h1 className="flex-1 text-lg sm:text-xl font-black tracking-wide text-center" style={{ fontFamily: "Manrope" }}>
          TOTAL JUGADA
        </h1>
        <div className="text-base font-black font-mono" data-testid="pos-classic-total">
          RD$ {cartTotal.toLocaleString("es-DO", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
        </div>
        <button
          onClick={printTicketNow}
          disabled={submitting || cart.length === 0}
          className="p-1.5 rounded bg-white/15 hover:bg-white/25 disabled:opacity-40"
          data-testid="pos-classic-print-btn"
          aria-label="Imprimir ticket"
        >
          <Printer className="w-6 h-6" strokeWidth={2.5} />
        </button>
        <button
          onClick={() => setMenuOpen(true)}
          className="p-1.5 rounded hover:bg-blue-700"
          data-testid="pos-classic-kebab"
          aria-label="Más opciones"
        >
          <MoreVertical className="w-6 h-6" strokeWidth={2.5} />
        </button>
      </header>

      {/* Current entry info row */}
      <div className="flex items-start justify-between px-4 py-3 border-b border-slate-200 bg-white" data-testid="pos-classic-current">
        <div>
          <div className="text-xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>
            <span data-testid="pos-classic-detected-pt">
              {autoDetectedPT?.name || "—"}
            </span>{" "}
            <span className="font-mono ml-1" data-testid="pos-classic-current-number">
              {currentNumber || "00"}
            </span>
          </div>
          <div className="text-base text-slate-500">
            {selectedGameObj?.name || "Sin lotería"}
            {activeField === "amount" && (
              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-black tracking-wider">
                ESCRIBIENDO MONTO
              </span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm font-bold text-slate-500 uppercase tracking-wider">Monto</div>
          <div className="text-xl font-black font-mono text-slate-900">
            RD$ {currentAmount || "0"}
          </div>
        </div>
      </div>

      {/* Cart list */}
      <div className="flex-1 overflow-y-auto bg-slate-50 px-3 pt-2 pb-2" data-testid="pos-classic-cart">
        {cart.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-slate-400 italic py-10">
            Aún no has agregado jugadas
          </div>
        ) : (
          <ul className="space-y-1.5">
            {cart.map((c, idx) => (
              <li
                key={c.id}
                data-testid={`pos-classic-cart-${idx}`}
                className="bg-white rounded-md border border-slate-200 px-3 py-2 flex items-center justify-between text-sm"
              >
                <div className="min-w-0">
                  <div className="font-mono font-black text-slate-900 text-base">
                    {c.display}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {c.pt_name} · {c.game_name}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-3">
                  <div className="font-mono font-black text-slate-900">RD${c.amount.toFixed(2)}</div>
                  <button
                    onClick={() => removeFromCart(c.id)}
                    className="w-7 h-7 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center"
                    data-testid={`pos-classic-cart-remove-${idx}`}
                    aria-label="Quitar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Amount input bar */}
      <div className="px-3 pt-2 pb-1 bg-white border-t border-slate-200 flex items-center gap-2">
        <input
          type="text"
          inputMode="decimal"
          value={currentAmount}
          onFocus={() => setActiveField("amount")}
          onChange={(e) => setCurrentAmount(e.target.value.replace(/[^0-9.]/g, ""))}
          placeholder={activeField === "amount" ? "Escribe el monto" : "Monto (RD$)"}
          className={`flex-1 bg-slate-50 border rounded-md px-3 py-2.5 text-lg font-mono font-bold outline-none ${
            activeField === "amount" ? "border-blue-500 ring-2 ring-blue-100" : "border-slate-300"
          }`}
          data-testid="pos-classic-amount-input"
        />
        <button
          onClick={entrar}
          disabled={activeField === "number" ? !currentNumber : !currentAmount}
          className="w-12 h-12 rounded-md bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white flex items-center justify-center"
          data-testid="pos-classic-amount-confirm"
          aria-label={activeField === "number" ? "Pasar al monto" : "Confirmar jugada"}
        >
          <Check className="w-6 h-6" strokeWidth={3} />
        </button>
      </div>

      {/* Lottery picker + sorteo info */}
      <div className="px-3 pb-2 pt-1 bg-white flex items-center gap-3">
        <button
          onClick={() => setLotteryOpen(true)}
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-black tracking-wide text-base py-3 rounded-md transition-colors"
          data-testid="pos-classic-lottery-btn"
        >
          LOTERÍA
        </button>
        <div className="text-blue-600 font-bold text-base whitespace-nowrap" data-testid="pos-classic-sorteo">
          {sorteoLabel}
        </div>
      </div>

      {/* Keypad */}
      <div className="px-2 pb-2 bg-white grid grid-cols-4 gap-1.5" data-testid="pos-classic-keypad">
        {/* Row 1 */}
        <ClassicKey label="1" onClick={() => pushDigit("1")} testId="pos-classic-key-1" />
        <ClassicKey label="2" onClick={() => pushDigit("2")} testId="pos-classic-key-2" />
        <ClassicKey label="3" onClick={() => pushDigit("3")} testId="pos-classic-key-3" />
        <ClassicKey icon={Shuffle} onClick={randomize} testId="pos-classic-random" />

        {/* Row 2 */}
        <ClassicKey label="4" onClick={() => pushDigit("4")} testId="pos-classic-key-4" />
        <ClassicKey label="5" onClick={() => pushDigit("5")} testId="pos-classic-key-5" />
        <ClassicKey label="6" onClick={() => pushDigit("6")} testId="pos-classic-key-6" />
        <ClassicKey
          icon={CombineIcon}
          onClick={() => setCombinarOpen(true)}
          disabled={!canCombine}
          testId="pos-classic-combinar"
          hint="COMBINAR"
        />

        {/* Row 3 */}
        <ClassicKey label="7" onClick={() => pushDigit("7")} testId="pos-classic-key-7" />
        <ClassicKey label="8" onClick={() => pushDigit("8")} testId="pos-classic-key-8" />
        <ClassicKey label="9" onClick={() => pushDigit("9")} testId="pos-classic-key-9" />
        <ClassicKey
          icon={X}
          onClick={() => { setCurrentNumber(""); setCurrentAmount(""); }}
          testId="pos-classic-clear"
          hint="Limpiar"
        />

        {/* Row 4 */}
        <ClassicKey icon={Delete} onClick={popDigit} testId="pos-classic-back" />
        <ClassicKey label="0" onClick={() => pushDigit("0")} testId="pos-classic-key-0" />
        <ClassicKey label="." onClick={() => pushDigit(".")} testId="pos-classic-key-dot" />
        <ClassicKey
          icon={Plus}
          onClick={() => {
            if (!currentAmount && cart.length > 0) {
              // Repeat last amount if quickly fanning numbers
              const last = cart[cart.length - 1].amount;
              setCurrentAmount(String(last));
            }
            setActiveField("number");
          }}
          testId="pos-classic-plus"
          hint="Repetir monto"
        />
      </div>

      {/* ENTRAR big button */}
      <div className="px-3 pt-1 pb-3 bg-white border-t border-slate-100">
        <button
          onClick={entrar}
          disabled={
            activeField === "number"
              ? !currentNumber || !selectedGameObj
              : !currentAmount
          }
          className="w-full h-14 rounded-md bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-black text-xl tracking-widest transition-colors"
          data-testid="pos-classic-entrar"
        >
          {activeField === "number" ? "ENTRAR" : "AGREGAR AL TICKET"}
        </button>
        {lastTicket && (
          <div className="mt-2 flex items-center justify-between text-xs font-mono bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-3 py-1.5" data-testid="pos-classic-last-ticket">
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4" />
              Último: <b>{lastTicket.ticket_number}</b>
            </span>
            <button
              onClick={() => printTicket(lastTicket, { isReprint: true })}
              className="font-black underline"
            >
              Reimprimir
            </button>
          </div>
        )}
      </div>

      {/* Slide-in menu drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 flex" data-testid="pos-classic-menu-drawer">
          <div className="w-72 bg-white border-r border-slate-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 bg-blue-600 text-white flex items-center justify-between">
              <div>
                <div className="text-lg font-black" style={{ fontFamily: "Manrope" }}>{businessName}</div>
                <div className="text-[11px] text-blue-100">Menú de usuario</div>
              </div>
              <button onClick={() => setMenuOpen(false)} className="p-1 rounded hover:bg-blue-700" aria-label="Cerrar">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 p-2 space-y-0.5">
              <ClassicMenuItem icon={History} label="Mis Tickets" onClick={() => navigate("/tickets")} testId="pos-classic-menu-history" />
              <ClassicMenuItem icon={ReceiptText} label="Resultados" onClick={() => navigate("/results")} testId="pos-classic-menu-results" />
              <ClassicMenuItem icon={Users} label="Pagar Premio" onClick={() => navigate("/payouts")} testId="pos-classic-menu-payouts" />
              <ClassicMenuItem icon={Eye} label="Modo Cliente" onClick={() => { setMenuOpen(false); setCustomerViewOpen(true); }} testId="pos-classic-menu-customer" />
              <ClassicMenuItem icon={SettingsIcon} label="Configuración" onClick={() => navigate("/settings")} testId="pos-classic-menu-settings" />
              <div className="h-px bg-slate-200 my-1.5" />
              <ClassicMenuItem icon={LogOut} label="Cerrar Sesión" danger onClick={logout} testId="pos-classic-menu-logout" />
            </div>
          </div>
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            className="flex-1 bg-slate-950/40 cursor-pointer"
            data-testid="pos-classic-menu-backdrop"
            aria-label="Cerrar menú"
          />
        </div>
      )}

      {/* Lottery picker sheet */}
      {lotteryOpen && (
        <LotterySheet
          onClose={() => setLotteryOpen(false)}
          operators={operatorsForGrid}
          openGamesByOp={openGamesByOp}
          selectedOperator={selectedOperator}
          setSelectedOperator={setSelectedOperator}
          selectedGame={selectedGame}
          setSelectedGame={setSelectedGame}
          availablePTs={availablePTs}
          selectedPT={selectedPT}
          setSelectedPT={setSelectedPT}
        />
      )}

      {/* Combinar modal */}
      {combinarOpen && (
        <CombinarSheet
          onClose={() => setCombinarOpen(false)}
          currentNumber={currentNumber}
          currentAmount={currentAmount}
          selectedGameObj={selectedGameObj}
          availablePTs={availablePTs}
          cart={cart}
          onAmountChange={setCurrentAmount}
          onAdd={(items) => {
            state.setCart((prev) => [...prev, ...items]);
            setCurrentNumber("");
            setCurrentAmount("");
            setActiveField("number");
            setCombinarOpen(false);
            toast.success(`${items.length} jugada${items.length === 1 ? "" : "s"} agregada${items.length === 1 ? "" : "s"}`);
          }}
        />
      )}
    </div>
  );
}

function ClassicKey({ label, icon: Icon, onClick, disabled, testId, hint }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="relative h-16 rounded-md bg-slate-100 hover:bg-slate-200 active:bg-slate-300 disabled:bg-slate-100 disabled:opacity-40 border border-slate-200 text-slate-900 flex items-center justify-center transition-colors"
    >
      {Icon ? <Icon className="w-6 h-6" strokeWidth={2.4} /> : (
        <span className="text-3xl font-black" style={{ fontFamily: "Manrope" }}>{label}</span>
      )}
      {hint && (
        <span className="absolute bottom-0.5 text-[9px] font-black tracking-wider text-slate-500 uppercase">
          {hint}
        </span>
      )}
    </button>
  );
}

function ClassicMenuItem({ icon: Icon, label, onClick, danger, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded hover:bg-slate-100 active:bg-slate-200 ${
        danger ? "text-red-600" : "text-slate-800"
      }`}
    >
      <Icon className="w-5 h-5" />
      <span className="font-bold text-sm">{label}</span>
    </button>
  );
}

/** Simple inline "combine" glyph using lucide Shuffle rotated — avoids extra deps. */
function CombineIcon({ className = "", strokeWidth = 2.4 }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 3 H5 a2 2 0 0 0 -2 2 v3" />
      <path d="M3 16 v3 a2 2 0 0 0 2 2 h3" />
      <path d="M16 21 h3 a2 2 0 0 0 2 -2 v-3" />
      <path d="M21 8 V5 a2 2 0 0 0 -2 -2 h-3" />
      <path d="M8 8 l8 8" />
      <path d="M16 8 l-8 8" />
    </svg>
  );
}

// Bottom-sheet to pick a lottery (operator → game) and play type.
function LotterySheet({
  onClose, operators, openGamesByOp,
  selectedOperator, setSelectedOperator, selectedGame, setSelectedGame,
  availablePTs, selectedPT, setSelectedPT,
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end" data-testid="pos-classic-lottery-sheet">
      <button
        type="button"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/50"
        aria-label="Cerrar"
      />
      <div className="relative w-full max-h-[82vh] overflow-y-auto bg-white rounded-t-2xl shadow-2xl flex flex-col animate-in slide-in-from-bottom duration-200">
        <div className="sticky top-0 bg-blue-600 text-white px-4 py-3 flex items-center justify-between">
          <h3 className="font-black text-base tracking-wide" style={{ fontFamily: "Manrope" }}>
            SELECCIONA LA LOTERÍA
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-blue-700" aria-label="Cerrar">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-3 space-y-3">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Operador</div>
            <div className="grid grid-cols-2 gap-2">
              {operators.length === 0 && (
                <div className="col-span-2 text-sm text-slate-500 italic">No hay loterías abiertas.</div>
              )}
              {operators.map((op) => {
                const sel = selectedOperator === op.id;
                const count = (openGamesByOp.get(op.id) || []).length;
                return (
                  <button
                    key={op.id}
                    onClick={() => { setSelectedOperator(op.id); setSelectedPT(null); }}
                    data-testid={`pos-classic-sheet-op-${op.code}`}
                    className={`px-3 py-2.5 rounded-md border-2 text-left transition-colors ${
                      sel ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-200 text-slate-800 active:bg-slate-50"
                    }`}
                  >
                    <div className="font-black text-sm">{op.name}</div>
                    <div className={`text-[11px] ${sel ? "text-blue-100" : "text-slate-500"}`}>
                      {count} juego{count === 1 ? "" : "s"} abierto{count === 1 ? "" : "s"}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          {selectedOperator && (openGamesByOp.get(selectedOperator) || []).length > 1 && (
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Juego</div>
              <div className="flex flex-wrap gap-1.5">
                {(openGamesByOp.get(selectedOperator) || []).map((g) => {
                  const sel = selectedGame === g.id;
                  return (
                    <button
                      key={g.id}
                      onClick={() => setSelectedGame(g.id)}
                      data-testid={`pos-classic-sheet-game-${g.code}`}
                      className={`px-3 py-1.5 rounded-md text-xs font-black ${
                        sel ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 active:bg-slate-200"
                      }`}
                    >
                      {g.name} · {g.closing_time}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Tipo de Jugada</div>
            <div className="grid grid-cols-2 gap-2">
              {availablePTs.map((pt) => {
                const sel = selectedPT?.id === pt.id;
                return (
                  <button
                    key={pt.id}
                    onClick={() => setSelectedPT(pt)}
                    data-testid={`pos-classic-sheet-pt-${pt.code}`}
                    className={`px-3 py-2 rounded-md border-2 ${
                      sel ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-200 text-slate-800 active:bg-slate-50"
                    }`}
                  >
                    <div className="font-black text-sm">{pt.name}</div>
                    <div className={`text-[11px] ${sel ? "text-blue-100" : "text-slate-500"}`}>
                      {pt.numbers_required} número{pt.numbers_required === 1 ? "" : "s"}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-full py-3 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-black tracking-wide"
            data-testid="pos-classic-sheet-done"
          >
            APLICAR
          </button>
        </div>
      </div>
    </div>
  );
}

// Combinar sheet (v2): The cashier has already entered some numbers — either
// typed in the current field or already in the ticket (e.g. several Quinielas).
// This modal asks for a single AMOUNT and a single COMBINATION TYPE
// (Palé/Tripleta/etc.), then auto-generates every nCr combination of those
// numbers for the picked type. One tap to review, one tap to add.
function CombinarSheet({ onClose, currentNumber, currentAmount, selectedGameObj, availablePTs, cart, onAdd, onAmountChange }) {
  // Collect candidate numbers from both sources (cart + currently typed).
  const sourceNumbers = useMemo(() => {
    if (!selectedGameObj) return [];
    const digits = selectedGameObj.number_digits;
    const seen = new Set();
    const result = [];
    // 1) Numbers already in the cart for the SAME game — any PT. We only use
    //    numbers of the right digit length (same game) and keep uniqueness.
    for (const item of cart || []) {
      if (item.game_id !== selectedGameObj.id) continue;
      for (const n of item.numbers || []) {
        if (n && n.length === digits && !seen.has(n)) {
          seen.add(n);
          result.push(n);
        }
      }
    }
    // 2) Numbers the cashier typed in the current field (not yet committed).
    const raw = (currentNumber || "").trim();
    if (raw) {
      let parts = [];
      if (/[-\s,]/.test(raw)) {
        parts = raw.split(/[-\s,]+/).filter((x) => x && x.length === digits && /^\d+$/.test(x));
      } else {
        for (let i = 0; i < raw.length; i += digits) {
          const p = raw.slice(i, i + digits);
          if (p.length === digits && /^\d+$/.test(p)) parts.push(p);
        }
      }
      for (const n of parts) {
        if (!seen.has(n)) {
          seen.add(n);
          result.push(n);
        }
      }
    }
    return result;
  }, [cart, currentNumber, selectedGameObj]);

  // Candidate combination types: all active PTs where numbers_required >= 2
  // (combining only makes sense from 2+) AND <= sourceNumbers.length.
  const candidatePTs = useMemo(() => {
    return availablePTs
      .filter((p) => p.numbers_required >= 2 && p.numbers_required <= sourceNumbers.length)
      .sort((a, b) => a.numbers_required - b.numbers_required);
  }, [availablePTs, sourceNumbers.length]);

  const [selectedComboPT, setSelectedComboPT] = useState(null);
  // Pick the first available type by default (usually Palé).
  useEffect(() => {
    if (!selectedComboPT && candidatePTs.length > 0) setSelectedComboPT(candidatePTs[0]);
    if (selectedComboPT && !candidatePTs.find((p) => p.id === selectedComboPT.id)) {
      setSelectedComboPT(candidatePTs[0] || null);
    }
  }, [candidatePTs]); // eslint-disable-line react-hooks/exhaustive-deps

  const [localAmount, setLocalAmount] = useState(currentAmount || "");
  useEffect(() => { setLocalAmount(currentAmount || ""); }, [currentAmount]);

  // Generate the preview combinations for the picked type.
  const previewRows = useMemo(() => {
    if (!selectedComboPT || sourceNumbers.length < selectedComboPT.numbers_required) return [];
    const amt = parseFloat(localAmount) || 0;
    const r = selectedComboPT.numbers_required;
    const out = [];
    const pick = (start, chosen) => {
      if (chosen.length === r) {
        out.push({
          id: `${selectedComboPT.id}-${chosen.join("-")}`,
          numbers: [...chosen],
          display: chosen.join("-"),
          amount: amt,
        });
        return;
      }
      for (let i = start; i < sourceNumbers.length; i++) {
        chosen.push(sourceNumbers[i]);
        pick(i + 1, chosen);
        chosen.pop();
      }
    };
    pick(0, []);
    return out;
  }, [selectedComboPT, sourceNumbers, localAmount]);

  const total = previewRows.length * (parseFloat(localAmount) || 0);

  const apply = () => {
    if (!selectedGameObj) return;
    if (!selectedComboPT) return toast.error("Elige el tipo de combinación");
    const amt = parseFloat(localAmount);
    if (!Number.isFinite(amt) || amt <= 0) return toast.error("Ingresa un monto válido");
    if (previewRows.length === 0) return toast.error("No hay combinaciones para generar");
    const items = previewRows.map((row) => ({
      id: Math.random().toString(36).slice(2),
      game_id: selectedGameObj.id,
      game_name: selectedGameObj.name,
      game_time: formatDrawTime(selectedGameObj.draw_time),
      play_type_id: selectedComboPT.id,
      play_type_code: selectedComboPT.code,
      pt_name: selectedComboPT.name,
      numbers: row.numbers,
      display: row.display,
      amount: amt,
    }));
    onAdd(items);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end" data-testid="pos-classic-combinar-sheet">
      <button
        type="button"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/50"
        aria-label="Cerrar"
      />
      <div className="relative w-full max-h-[90vh] overflow-y-auto bg-white rounded-t-2xl shadow-2xl flex flex-col animate-in slide-in-from-bottom duration-200">
        <div className="sticky top-0 bg-blue-600 text-white px-4 py-3 flex items-center justify-between">
          <div>
            <h3 className="font-black text-base tracking-wide" style={{ fontFamily: "Manrope" }}>
              COMBINAR
            </h3>
            <div className="text-[11px] text-blue-100">
              {selectedGameObj?.name || "Sin lotería"} · {sourceNumbers.length} número{sourceNumbers.length === 1 ? "" : "s"} disponible{sourceNumbers.length === 1 ? "" : "s"}
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-blue-700" aria-label="Cerrar">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Source numbers */}
          <div className="bg-blue-50 border border-blue-200 rounded-md px-3 py-2 text-sm">
            <div className="text-[11px] font-bold uppercase tracking-wider text-blue-900 mb-0.5">
              Números a combinar
            </div>
            <div className="font-mono font-black text-blue-700 flex flex-wrap gap-x-2 gap-y-0.5" data-testid="pos-classic-combinar-source">
              {sourceNumbers.length === 0 ? "— ninguno —" : sourceNumbers.join(" · ")}
            </div>
          </div>

          {/* Amount editor */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block mb-1.5">
              Monto por jugada combinada
            </label>
            <div className="flex items-center gap-1 bg-white border-2 border-slate-300 rounded-md px-3 py-2.5 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100">
              <span className="text-base font-bold text-slate-500">RD$</span>
              <input
                type="text"
                inputMode="decimal"
                autoFocus
                value={localAmount}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9.]/g, "");
                  setLocalAmount(v);
                  if (onAmountChange) onAmountChange(v);
                }}
                placeholder="0.00"
                className="flex-1 bg-transparent text-2xl font-mono font-black text-slate-900 outline-none text-right"
                data-testid="pos-classic-combinar-amount"
              />
            </div>
          </div>

          {/* Combination type picker */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block mb-1.5">
              Tipo de combinación
            </label>
            {candidatePTs.length === 0 ? (
              <div className="text-sm text-slate-500 italic py-3 text-center bg-slate-50 rounded-md border border-slate-200">
                Necesitas al menos 2 números para combinar.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {candidatePTs.map((pt) => {
                  const sel = selectedComboPT?.id === pt.id;
                  // Number of combinations this choice would generate
                  const nCr = (n, r) => {
                    let num = 1, den = 1;
                    for (let i = 0; i < r; i++) { num *= (n - i); den *= (i + 1); }
                    return Math.round(num / den);
                  };
                  const count = nCr(sourceNumbers.length, pt.numbers_required);
                  return (
                    <button
                      key={pt.id}
                      type="button"
                      onClick={() => setSelectedComboPT(pt)}
                      data-testid={`pos-classic-combinar-type-${pt.code}`}
                      className={`px-3 py-3 rounded-md border-2 text-left transition-colors ${
                        sel
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "bg-white border-slate-200 text-slate-800 active:bg-slate-50"
                      }`}
                    >
                      <div className="font-black text-base">{pt.name}</div>
                      <div className={`text-[11px] ${sel ? "text-blue-100" : "text-slate-500"}`}>
                        {count} combinación{count === 1 ? "" : "es"} · {pt.numbers_required} núm.
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Preview */}
          {previewRows.length > 0 && (
            <div data-testid="pos-classic-combinar-preview">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                Vista previa ({selectedComboPT?.name})
              </div>
              <ul className="border border-slate-200 rounded-md divide-y divide-slate-100 bg-white max-h-56 overflow-y-auto">
                {previewRows.map((r, idx) => (
                  <li
                    key={r.id}
                    data-testid={`pos-classic-combinar-preview-${idx}`}
                    className="flex items-center justify-between px-3 py-2 text-sm"
                  >
                    <span className="font-mono font-black text-slate-900">{r.display}</span>
                    <span className="font-mono font-bold text-slate-700">RD$ {(parseFloat(localAmount) || 0).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 py-3 flex items-center gap-3">
          <div className="flex-1">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
              {previewRows.length} jugada{previewRows.length === 1 ? "" : "s"}
            </div>
            <div className="text-xl font-black font-mono text-slate-900">
              Total RD$ {total.toFixed(2)}
            </div>
          </div>
          <button
            onClick={apply}
            disabled={previewRows.length === 0 || !localAmount}
            className="h-12 px-6 rounded-md bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-black tracking-wide"
            data-testid="pos-classic-combinar-apply"
          >
            AGREGAR
          </button>
        </div>
      </div>
    </div>
  );
}
